import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../lib/timezones.js';
import { HeaderActions } from '../lib/pageHeader.jsx';
import Pagination, { PAGE_SIZE } from '../components/ui/Pagination.jsx';
import ApiKeyForm from './ApiKeyForm.jsx';

const ENDPOINT_TONE = {
  planet: 'bg-emerald-50 text-emerald-700',
  terrain: 'bg-amber-50 text-amber-700',
  satellite: 'bg-indigo-50 text-indigo-700',
  elevation: 'bg-sky-50 text-sky-700',
  editor: 'bg-fuchsia-50 text-fuchsia-700'
};

function Chip({ children, tone = 'bg-ink-100 text-ink-700' }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {children}
    </span>
  );
}

// Compact 7-day usage summary. All zeros => em-dash so idle keys don't draw
// attention. last_day is shown small when anything is non-zero.
function UsageCell({ u }) {
  if (!u || ((u.ok | 0) + (u.deny | 0) + (u.rate | 0)) === 0) {
    return <span className="text-ink-400">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1 font-mono text-[11px]">
        <span className="text-emerald-700">{u.ok}</span>
        <span className="text-ink-300">·</span>
        <span className={u.deny ? 'text-red-700' : 'text-ink-400'}>{u.deny} deny</span>
        {!!u.rate && (
          <>
            <span className="text-ink-300">·</span>
            <span className="text-amber-700">{u.rate} rate</span>
          </>
        )}
      </div>
      {u.last_day && <div className="text-[10px] text-ink-400">last: {u.last_day}</div>}
    </div>
  );
}

function maskKey(v) {
  if (!v) return '';
  if (v.length <= 16) return v;
  return `${v.slice(0, 8)}…${v.slice(-4)}`;
}

export default function ApiKeys() {
  const { user } = useAuth();
  const tz = user?.timezone || DEFAULT_TIMEZONE;
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealed, setRevealed] = useState(new Set());
  const [banner, setBanner] = useState(null); // { keyValue }

  async function load(opts = {}) {
    const off = opts.offset ?? offset;
    setError('');
    try {
      const r = await api.get(`/api-keys?limit=${PAGE_SIZE}&offset=${off}`);
      setItems(r.items ?? []);
      setTotal(r.total ?? 0);
      setOffset(r.offset ?? 0);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => { load({ offset: 0 }); }, []);

  function openNew() { setEditing(null); setFormOpen(true); }
  function openEdit(row) { setEditing(row); setFormOpen(true); }

  async function handleSaved(saved, { isNew }) {
    setFormOpen(false);
    if (isNew) setBanner({ keyValue: saved.key_value });
    await load();
  }

  async function toggle(row) {
    await api.post(`/api-keys/${row.id}/toggle`);
    await load();
  }

  // Rotate the raw value when a key is suspected of being leaked. Shows
  // the new value in the top banner (same UX as create) — old value
  // stops working immediately server-side.
  async function regenerate(row) {
    const label = row.key_name || `key #${row.id}`;
    if (!confirm(
      `Regenerate key "${label}"?\n\n` +
      `The current key value will stop working immediately. All other ` +
      `settings (owner, endpoints, limits, expiry) are preserved.`
    )) return;
    try {
      const saved = await api.post(`/api-keys/${row.id}/regenerate`);
      setBanner({ keyValue: saved.key_value, regenerated: true });
      await load();
    } catch (e) {
      alert(e?.data?.error ?? e.message);
    }
  }

  async function remove(row) {
    if (!confirm(`Delete key "${row.key_name || row.key_value}"? This cannot be undone.`)) return;
    await api.delete(`/api-keys/${row.id}`);
    await load();
  }

  function toggleReveal(id) {
    setRevealed((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function copy(text) {
    try { await navigator.clipboard.writeText(text); }
    catch { /* ignore */ }
  }

  return (
    <div className="space-y-4">
      <HeaderActions>
        <button className="btn-primary" onClick={openNew}>
          <i className="fa-solid fa-plus" /> New key
        </button>
      </HeaderActions>

      <div>
        <p className="text-sm text-ink-500">Issue and revoke keys for tile endpoints.</p>
      </div>

      {banner && (
        <div className="card flex items-start gap-3 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <i className="fa-solid fa-circle-check mt-0.5 text-emerald-600" />
          <div className="flex-1">
            <div className="font-semibold">
              {banner.regenerated
                ? 'Key regenerated. Copy the new value now — it is shown only once.'
                : 'Key created. Copy it now — full value is shown only once.'}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-xs">{banner.keyValue}</code>
              <button className="btn-ghost" onClick={() => copy(banner.keyValue)} title="Copy">
                <i className="fa-solid fa-copy" />
              </button>
              <button className="btn-ghost" onClick={() => setBanner(null)} title="Dismiss">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="card p-4 text-sm text-red-700">{error}</div>}

      <div className="card overflow-hidden">
        {items === null ? (
          <div className="p-6 text-sm text-ink-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-500">
            <i className="fa-solid fa-key mb-2 text-2xl text-ink-300" />
            <div>No API keys yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50 text-left text-xs font-semibold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-2.5">Name / Key</th>
                  <th className="px-4 py-2.5">Owner</th>
                  <th className="px-4 py-2.5">Endpoints</th>
                  <th className="px-4 py-2.5">Origins / IPs</th>
                  <th className="px-4 py-2.5 text-right">Rate</th>
                  <th className="px-4 py-2.5">Usage 7d</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const endpoints = row.allowed_endpoints ? row.allowed_endpoints.split(',') : [];
                  const origins = row.allowed_origins ? row.allowed_origins.split(',') : [];
                  const ips = row.allowed_ips ? row.allowed_ips.split(',') : [];
                  const isRevealed = revealed.has(row.id);
                  return (
                    <tr key={row.id} className="border-b border-ink-100 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink-900">{row.key_name || <em className="text-ink-400">(unnamed)</em>}</div>
                        <div className="mt-0.5 flex items-center gap-1.5 font-mono text-xs text-ink-500">
                          <span>{isRevealed ? row.key_value : maskKey(row.key_value)}</span>
                          <button className="text-ink-400 hover:text-ink-700" onClick={() => toggleReveal(row.id)} title={isRevealed ? 'Hide' : 'Reveal'}>
                            <i className={`fa-solid ${isRevealed ? 'fa-eye-slash' : 'fa-eye'}`} />
                          </button>
                          <button className="text-ink-400 hover:text-ink-700" onClick={() => copy(row.key_value)} title="Copy">
                            <i className="fa-solid fa-copy" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-900">{row.owner_email}</div>
                        {row.owner_name && <div className="text-xs text-ink-500">{row.owner_name}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {endpoints.length === 0
                            ? <Chip tone="bg-ink-100 text-ink-500">any</Chip>
                            : endpoints.map((e) => <Chip key={e} tone={ENDPOINT_TONE[e]}>{e}</Chip>)}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        <div className="flex flex-wrap gap-1">
                          {origins.length === 0
                            ? <Chip tone="bg-ink-100 text-ink-500">any origin</Chip>
                            : origins.map((o) => <Chip key={o}>{o}</Chip>)}
                        </div>
                        {ips.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {ips.map((ip) => (
                              <Chip key={ip} tone="bg-sky-50 text-sky-700">
                                <i className="fa-solid fa-network-wired mr-1 text-[9px]" />{ip}
                              </Chip>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-700">
                        {row.rate_limit > 0 ? `${row.rate_limit}/min` : '∞'}
                      </td>
                      <td className="px-4 py-3">
                        <UsageCell u={row.usage_7d} />
                      </td>
                      <td className="px-4 py-3">
                        {row.is_active
                          ? <Chip tone="bg-emerald-50 text-emerald-700">active</Chip>
                          : <Chip tone="bg-ink-200 text-ink-600">disabled</Chip>}
                        {row.expires_at && row.expires_at <= Date.now() && (
                          <Chip tone="bg-red-50 text-red-700">expired</Chip>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">
                        {formatInTz(row.created_at, tz)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button className="btn-ghost p-2" onClick={() => openEdit(row)} title="Edit">
                            <i className="fa-solid fa-pen-to-square" />
                          </button>
                          <button className="btn-ghost p-2" onClick={() => toggle(row)} title={row.is_active ? 'Disable' : 'Enable'}>
                            <i className={`fa-solid ${row.is_active ? 'fa-pause' : 'fa-play'}`} />
                          </button>
                          <button
                            className="btn-ghost p-2 text-amber-600 hover:bg-amber-50"
                            onClick={() => regenerate(row)}
                            title="Regenerate key value (use when compromised)"
                          >
                            <i className="fa-solid fa-arrows-rotate" />
                          </button>
                          <button className="btn-ghost p-2 text-red-600 hover:bg-red-50" onClick={() => remove(row)} title="Delete">
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {items && items.length > 0 && (
          <Pagination
            total={total}
            offset={offset}
            limit={PAGE_SIZE}
            onChange={(o) => load({ offset: o })}
          />
        )}
      </div>

      {formOpen && (
        <ApiKeyForm
          editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
